<template>
  <div class="edit-lesson-container">
    <div class="edit-header">
      <h2 class="edit-title">Edit Lesson</h2>
      <button @click="$emit('close')" class="close-button" title="Close">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="close-icon">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <form @submit.prevent="submitForm" class="edit-form">
      <div class="form-group">
        <label for="title" class="form-label">Title</label>
        <input 
          type="text" 
          id="title" 
          v-model="form.title" 
          class="form-input"
          required
        >
      </div>

      <div class="form-group">
        <label for="title_ar" class="form-label">Title Arabic</label>
        <input 
          type="text" 
          id="title_ar" 
          v-model="form.title_ar" 
          class="form-input"
          placeholder="Title in Arabic"
        >
      </div>

      <div class="form-group">
        <label for="description" class="form-label">Description</label>
        <textarea 
          id="description" 
          v-model="form.description" 
          class="form-textarea"
          rows="4"
          placeholder="Description"
        ></textarea>
      </div>

      <div class="form-group">
        <label for="description_ar" class="form-label">Description Arabic</label>
        <textarea 
          id="description_ar" 
          v-model="form.description_ar" 
          class="form-textarea"
          rows="4"
          placeholder="Description in Arabic"
        ></textarea>
      </div>
      
      <div class="form-row">
        <div class="form-group">
          <label for="duration" class="form-label">Duration (minutes)</label>
          <input 
            type="number" 
            id="duration" 
            v-model="form.duration" 
            class="form-input"
            min="1"
            required
          >
        </div>
        
        <div class="form-group">
          <label for="status" class="form-label">Status</label>
          <select 
            id="status" 
            v-model="form.lesson_status" 
            class="form-select"
            required
          >
            <option value="not_started">Not Started</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="uploaded">Uploaded</option>
          </select>
        </div>
      </div>
      
      <div class="form-group">
        <label for="course" class="form-label">Course</label>
        <select 
          id="course" 
          v-model="form.course_id" 
          class="form-select"
        >
          <option value="">Select a course</option>
          <option 
            v-for="course in courses" 
            :key="course.id" 
            :value="course.id"
          >
            {{ course.title }}
          </option>
        </select>
      </div>
      
      <div class="form-group">
        <label class="form-label">Thumbnail</label>
        <div class="thumbnail-upload">
          <div class="thumbnail-preview" v-if="form.lesson_temp_file">
            <img 
              :src="form.lesson_temp_file" 
              :alt="form.title"
              class="preview-img"
            >
            <button 
              type="button" 
              @click="removeThumbnail" 
              class="remove-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="remove-icon">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="upload-placeholder" v-else>
            <input 
              type="file" 
              ref="fileInput" 
              @change="handleFileUpload" 
              accept="image/*"
              class="file-input"
            >
            <div class="upload-content">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="upload-icon">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <span class="upload-text">Click to upload thumbnail</span>
            </div>
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button type="button" @click="$emit('close')" class="cancel-button">
          Cancel
        </button>
        <button type="submit" class="save-button" :disabled="isSubmitting">
          <span v-if="isSubmitting">Saving...</span>
          <span v-else>Save Changes</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
export default {
  name: 'EditLesson',
  props: {
    lesson: {
      type: Object,
      default: null
    },
    courses: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      form: {
        title: '',
        description: '',
        title_ar: '',
        description_ar: '',
        duration: 0,
        lesson_status: '',
        course_id: null,
        lesson_temp_file: null
      },
      isSubmitting: false
    }
  },
  watch: {
    lesson: {
      handler(newLesson) {
        if (newLesson) {
          this.form = {
            title: newLesson.title,
            description: newLesson.description,
            title_ar: newLesson.title_ar,
            description_ar: newLesson.description_ar,
            duration: newLesson.duration,
            lesson_status: newLesson.lesson_status,
            course_id: newLesson.course?.id || null,
            lesson_temp_file: newLesson.lesson_temp_file
          };
        }
      },
      immediate: true
    }
  },
  methods: {
    submitForm() {
      this.isSubmitting = true;
      this.$emit('save', this.form);
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.form.lesson_temp_file = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    removeThumbnail() {
      this.form.lesson_temp_file = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    }
  }
}
</script>

<style scoped>
.edit-lesson-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  overflow: hidden;
}

.edit-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.edit-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.close-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: #e2e8f0;
}

.close-icon {
  width: 18px;
  height: 18px;
}

.edit-form {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
  margin-bottom: 0.5rem;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9375rem;
  color: #1e293b;
  background: white;
  transition: all 0.2s ease;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.thumbnail-upload {
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.2s ease;
}

.thumbnail-upload:hover {
  border-color: #cbd5e1;
}

.thumbnail-preview {
  position: relative;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
}

.preview-img {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.remove-button {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: white;
  color: #ef4444;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.remove-button:hover {
  transform: scale(1.1);
}

.remove-icon {
  width: 14px;
  height: 14px;
}

.upload-placeholder {
  cursor: pointer;
}

.file-input {
  display: none;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.upload-icon {
  width: 32px;
  height: 32px;
  color: #94a3b8;
}

.upload-text {
  font-size: 0.875rem;
  color: #64748b;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.cancel-button {
  padding: 0.75rem 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #475569;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-button:hover {
  background: #f8fafc;
}

.save-button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
}

.save-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);
}

.save-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .edit-form {
    padding: 1rem;
  }
}
</style> 